import useDeepCompareEffect from 'use-deep-compare-effect';

import { followedSearchPageItemCard } from '@/components/GridItemCard';
import FluidItemGrid from '@/components/GridItemCard/FluidItemGrid/FluidItemGrid';
import { MyFavoritesNoResults } from '@/components/MyFavoritesNoResults/MyFavoritesNoResults';
import Pagination from '@/components/Pagination/hammer-pagination/Pagination';
import { SearchFollowSuggestions } from '@/components/SearchFollowSuggestions/SearchFollowSuggestions';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getMySavedSearchItemIds } from '@/redux/modules/filteredItems';
import { fetchItemSummariesIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import {
    getTotalSavedSearches,
    getTotalSavedSearchItems,
    isSavedSearchItemsLoading,
} from '@/redux/modules/savedSearch';
import { PaginationFilter } from '@/types/PaginationFilter';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import SavedSearchFilter from './SavedSearchFilter';

type Props = {
    filters: PaginationFilter;
    onSavedSearchRecentFilterChange: (filters: PaginationFilter) => void;
};

export const MySavedSearchPageRecentItems = (props: Props) => {
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();
    const { filters, onSavedSearchRecentFilterChange } = props;

    const isLoading = useAppSelector(isSavedSearchItemsLoading);
    const itemIds = useAppSelector((state) =>
        getMySavedSearchItemIds(state, {
            count: filters.pageSize,
            page: filters.page || 1,
            terms: filters.keyword,
        })
    );
    const totalSavedSearchesCount = useAppSelector(getTotalSavedSearches);
    const totalSavedSearchItemsCount = useAppSelector(getTotalSavedSearchItems);
    const showPager = typeof filters.pageSize === 'number' && totalSavedSearchItemsCount > filters.pageSize;

    useDeepCompareEffect(() => {
        dispatch(
            fetchItemSummariesIfNeeded({
                identifier: 'MY-SAVED-SEARCH-PAGE',
                itemIds,
            })
        );
    }, [dispatch, itemIds]);

    const filterComponent = (
        <SavedSearchFilter
            filters={filters}
            onFilterChange={onSavedSearchRecentFilterChange}
        />
    );

    const gridComponent = (
        <FluidItemGrid
            className="max-[560px]:mb-sm mt-lg"
            itemCardOption={followedSearchPageItemCard}
            itemIds={itemIds}
        />
    );

    return (
        <div data-testid="my-saved-search-page-recent-items">
            {showPager ? (
                <Pagination
                    filter={filterComponent}
                    onChange={onSavedSearchRecentFilterChange}
                    paginationFilter={{
                        ...filters,
                        totalRecords: totalSavedSearchItemsCount,
                    }}
                    shouldModifyQueryParams
                >
                    {gridComponent}
                </Pagination>
            ) : (
                <>
                    {filterComponent}
                    {!isLoading && !itemIds.length ? (
                        <MyFavoritesNoResults>
                            {formatMessage(['empty_recent_results_message_part_1'])}
                        </MyFavoritesNoResults>
                    ) : (
                        gridComponent
                    )}
                </>
            )}

            {!isLoading && totalSavedSearchesCount === 0 && (
                <div className="my-lg text-center text-sm tracking-[0.23px]">
                    {formatMessage(['search_alerts', 'no_followed'])}
                </div>
            )}
            {totalSavedSearchesCount <= 0 && (
                <SearchFollowSuggestions
                    className="w-full [&>div[class*='altHeader']]:mx-0 [&>div[class*='altHeader']]:mt-0 [&>div[class*='altHeader']]:mb-[20px] [&>div[class*='side-scroller']]:w-full"
                    useAltHeader
                />
            )}
        </div>
    );
};
